import { useEffect, useState } from 'react';
import { H2 } from 'foundations-components/transitional/components/H2';
import { DialogCloseButton } from 'foundations-components/transitional/components/DialogCloseButton';
import { DialogHeader } from 'foundations-components/transitional/components/DialogHeader';
import { ModalPanel } from 'foundations-components/transitional/components/ModalPanel';
import FormattedMessage from 'I18n/components/FormattedMessage';
import formatName from 'I18n/utils/formatName';
import { DialogFooter } from 'foundations-components/transitional/components/DialogFooter';
import { LoadingButton } from 'foundations-components/transitional/components/LoadingButton';
import { Button } from 'foundations-components/transitional/components/Button';
import { DialogBody } from 'foundations-components/transitional/components/DialogBody';
import { FormControl } from 'foundations-components/transitional/components/FormControl';
import { TextInput } from 'foundations-components/transitional/components/TextInput';
import { CurrencyInput } from 'foundations-components/transitional/components/CurrencyInput';
import { CommerceTool } from '../../../common/constants/commerceTool';
import { Modal } from 'foundations-components/transitional/components/Modal';
import { useGetStoredPaymentMethodForContact } from '../hooks/useGetStoredPaymentMethodForContact';
import { LoadingSpinner } from 'foundations-components/transitional/components/LoadingSpinner';
import { Flex } from 'foundations-components/components/Flex';
import { useCreateOnFileCheckoutSession } from '../hooks/useCreateOnFileCheckoutSession';
import { usePayOnFileCheckoutSession } from '../hooks/usePayOnFileCheckoutSession';
import Raven from 'raven-js';
import getChargePaymentMethodFailureMessage from '../utils/getChargePaymentMethodFailureMessage';
import getStoredPaymentDisplayText from '../utils/getStoredPaymentDisplayText';
import { View } from 'foundations-components/components/View';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const ChargePaymentMethodPanel = ({
  amount,
  currencyCode = 'USD',
  contact,
  source,
  onSuccess,
  onReject,
  onFailure
}) => {
  const [showFailureModal, setShowFailureModal] = useState(false);
  const [errorCode, setErrorCode] = useState(null);
  const {
    data: storedPaymentMethodData,
    loading: loadingStoredPaymentMethods
  } = useGetStoredPaymentMethodForContact(Number(contact.id));
  const storedPaymentMethod = (storedPaymentMethodData === null || storedPaymentMethodData === void 0 ? void 0 : storedPaymentMethodData.getActivePaymentMethodOnFileListForContact.paymentMethodOnFileList) && storedPaymentMethodData.getActivePaymentMethodOnFileListForContact.paymentMethodOnFileList[0];
  useEffect(() => {
    if (!loadingStoredPaymentMethods && !storedPaymentMethod) {
      // validation in parent should prevent reaching this state
      // Parent component will close this panel, display an error and refetch validation
      onFailure();
    }
  }, [loadingStoredPaymentMethods, onFailure, storedPaymentMethod]);
  const storedPaymentMethodDisplayText = storedPaymentMethod ? getStoredPaymentDisplayText(storedPaymentMethod) : '';
  const contactValue = formatName({
    firstName: contact.firstname,
    lastName: contact.lastname,
    email: contact.email
  });
  const [createOnFileCheckoutSession, {
    loading: createOnFileCheckoutSessionSubmitting
  }] = useCreateOnFileCheckoutSession();
  const [payOnFileCheckoutSession, {
    loading: payOnFileCheckoutSessionSubmitting
  }] = usePayOnFileCheckoutSession();
  const submitting = createOnFileCheckoutSessionSubmitting || payOnFileCheckoutSessionSubmitting;
  const chargeInvoiceWithPMOF = async () => {
    if (storedPaymentMethod) {
      try {
        var _createOnFileSessionR, _payResponse$data;
        const createOnFileSessionResponse = await createOnFileCheckoutSession({
          variables: {
            invoiceId: source.crmId,
            paymentMethodOnFileId: storedPaymentMethod.id.toString()
          }
        });
        const checkoutSessionId = createOnFileSessionResponse === null || createOnFileSessionResponse === void 0 || (_createOnFileSessionR = createOnFileSessionResponse.data) === null || _createOnFileSessionR === void 0 || (_createOnFileSessionR = _createOnFileSessionR.createOnFileCheckoutSession) === null || _createOnFileSessionR === void 0 ? void 0 : _createOnFileSessionR.checkoutSessionId;
        if (!checkoutSessionId) {
          throw new Error('Failed to create on-file checkout session');
        }
        const payResponse = await payOnFileCheckoutSession({
          variables: {
            checkoutSessionId,
            expectedPaymentAmount: amount
          }
        });
        const status = payResponse === null || payResponse === void 0 || (_payResponse$data = payResponse.data) === null || _payResponse$data === void 0 || (_payResponse$data = _payResponse$data.payOnFileCheckoutSession) === null || _payResponse$data === void 0 ? void 0 : _payResponse$data.status;
        if (!status) {
          throw new Error('Failed to charge payment method, no status in response');
        }
        if (status === 'FAILED') {
          var _payResponse$data2;
          setShowFailureModal(true);
          if (payResponse !== null && payResponse !== void 0 && (_payResponse$data2 = payResponse.data) !== null && _payResponse$data2 !== void 0 && (_payResponse$data2 = _payResponse$data2.payOnFileCheckoutSession) !== null && _payResponse$data2 !== void 0 && _payResponse$data2.failureDetails) {
            setErrorCode(payResponse.data.payOnFileCheckoutSession.failureDetails.declineCode || payResponse.data.payOnFileCheckoutSession.failureDetails.failureCode);
          }
        } else if (['SUCCEEDED', 'PENDING'].includes(status)) {
          onSuccess(status);
        } else {
          throw new Error('Unexpected status return from payments-checkout/v1/checkout-sessions/on-file/pay');
        }
      } catch (error) {
        Raven.captureException(new Error('Failed to charge payment method'));
        onFailure();
      }
    } else {
      Raven.captureException(new Error('Missing stored payment method'));
      onFailure();
    }
  };
  const handleSubmit = () => {
    void chargeInvoiceWithPMOF();
  };
  const failureNextStepMessageKey = source.type === CommerceTool.SUBSCRIPTIONS ? 'commerceSharedComponents.ChargePaymentMethodPanel.failureModal.nextStep.invoiceFromSubscription' : 'commerceSharedComponents.ChargePaymentMethodPanel.failureModal.nextStep.invoice';
  if (showFailureModal && errorCode) {
    return /*#__PURE__*/_jsxs(Modal, {
      use: "success",
      onEsc: onReject,
      children: [/*#__PURE__*/_jsxs(DialogHeader, {
        children: [/*#__PURE__*/_jsx(H2, {
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "commerceSharedComponents.ChargePaymentMethodPanel.failureModal.header"
          })
        }), /*#__PURE__*/_jsx(DialogCloseButton, {
          onClick: onReject
        })]
      }), /*#__PURE__*/_jsxs(DialogBody, {
        paddingBlockEnd: "600",
        children: [/*#__PURE__*/_jsx(FormattedMessage, {
          message: getChargePaymentMethodFailureMessage(errorCode),
          options: {
            sourceId: source.sourceId,
            last4: storedPaymentMethod === null || storedPaymentMethod === void 0 ? void 0 : storedPaymentMethod.paymentMethodDetails.last4
          }
        }), /*#__PURE__*/_jsx(View, {
          paddingBlockStart: "600",
          as: "div",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: failureNextStepMessageKey
          })
        })]
      })]
    });
  }
  return /*#__PURE__*/_jsxs(ModalPanel, {
    "data-test-id": "charge-stored-payment-method-panel",
    onEsc: onReject,
    children: [/*#__PURE__*/_jsxs(DialogHeader, {
      children: [/*#__PURE__*/_jsx(H2, {
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "commerceSharedComponents.ChargePaymentMethodPanel.title"
        })
      }), /*#__PURE__*/_jsx(DialogCloseButton, {
        onClick: onReject,
        disabled: submitting
      })]
    }), /*#__PURE__*/_jsx(DialogBody, {
      children: loadingStoredPaymentMethods ? /*#__PURE__*/_jsx(Flex, {
        blockSize: "100%",
        inlineSize: "100%",
        justifyContent: "center",
        alignItems: "center",
        children: /*#__PURE__*/_jsx(LoadingSpinner, {
          size: "md"
        })
      }) : /*#__PURE__*/_jsxs(_Fragment, {
        children: [/*#__PURE__*/_jsx(FormControl, {
          label: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "commerceSharedComponents.ChargePaymentMethodPanel.contactLabel"
          }),
          children: /*#__PURE__*/_jsx(TextInput, {
            value: contactValue,
            readOnly: true
          })
        }), /*#__PURE__*/_jsx(FormControl, {
          label: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "commerceSharedComponents.ChargePaymentMethodPanel.paymentMethodLabel"
          }),
          children: /*#__PURE__*/_jsx(TextInput, {
            value: storedPaymentMethodDisplayText,
            readOnly: true
          })
        }), /*#__PURE__*/_jsx(FormControl, {
          label: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "commerceSharedComponents.ChargePaymentMethodPanel.amountLabel"
          }),
          children: /*#__PURE__*/_jsx(CurrencyInput, {
            currency: currencyCode,
            value: amount,
            readOnly: true
          })
        })]
      })
    }), /*#__PURE__*/_jsxs(DialogFooter, {
      children: [/*#__PURE__*/_jsx(LoadingButton, {
        use: "primary",
        onClick: handleSubmit,
        "data-test-id": "charge-stored-payment-method-panel-confirm-button",
        loading: submitting,
        disabled: loadingStoredPaymentMethods,
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "commerceSharedComponents.ChargePaymentMethodPanel.confirmButton"
        })
      }), /*#__PURE__*/_jsx(Button, {
        onClick: onReject,
        disabled: submitting,
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "commerceSharedComponents.ChargePaymentMethodPanel.cancelButton"
        })
      })]
    })]
  });
};
export default ChargePaymentMethodPanel;